import { useRef, type KeyboardEvent, type PointerEvent } from 'react';

import styles from './horizontal-scrollbar.module.css';
import type { HorizontalScrollMetrics } from './useHorizontalScroll';

interface HorizontalScrollbarProps {
  className?: string;
  ariaLabel?: string;
  metrics: HorizontalScrollMetrics;
  onScrollLeftChange: (next: number) => void;
}

/**
 * 自绘的水平滚动条：横向溢出时显示，支持拖动滑块、点轨道跳转、方向键与 Home/End。
 *
 * 渲染位置在滚动容器**外面**（表体下方、分页上方），所以滑块位置由容器的度量算出来，
 * 拖动再回写 `scrollLeft`——这样滚动条本身不占用表体的高度，也不会被表格内容盖住。
 * 引擎无关：原生条已被容器藏掉，两端（共享 Table / 订单预览）用的是同一份组件与样式。
 */
export function HorizontalScrollbar({ ariaLabel = '表格横向滚动', className, metrics, onScrollLeftChange }: HorizontalScrollbarProps) {
  const dragOffsetRef = useRef<number | null>(null);
  const maxScrollLeft = Math.max(0, metrics.scrollWidth - metrics.clientWidth);
  // 轨道全长 100%，滑块最少留 12%，否则内容极长时滑块会细成一条线、抓不住。
  const thumbWidth = maxScrollLeft > 0 ? Math.max(12, (metrics.clientWidth / metrics.scrollWidth) * 100) : 100;
  const thumbLeft = maxScrollLeft > 0 ? (metrics.scrollLeft / maxScrollLeft) * (100 - thumbWidth) : 0;

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const step = Math.max(40, metrics.clientWidth * 0.2);
    if (event.key === 'ArrowLeft') onScrollLeftChange(metrics.scrollLeft - step);
    else if (event.key === 'ArrowRight') onScrollLeftChange(metrics.scrollLeft + step);
    else if (event.key === 'Home') onScrollLeftChange(0);
    else if (event.key === 'End') onScrollLeftChange(maxScrollLeft);
    else return;
    event.preventDefault();
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    const track = event.currentTarget;
    const bounds = track.getBoundingClientRect();
    const pointerX = event.clientX - bounds.left;
    const thumbOffset = (thumbLeft / 100) * bounds.width;
    const thumbPixelWidth = (thumbWidth / 100) * bounds.width;
    // 点在滑块上按「抓住原来那个位置」拖，点在轨道空白处则让滑块中心跟过去。
    dragOffsetRef.current = event.target === track ? thumbPixelWidth / 2 : pointerX - thumbOffset;
    track.setPointerCapture(event.pointerId);
    onScrollLeftChange(((pointerX - dragOffsetRef.current) / Math.max(1, bounds.width - thumbPixelWidth)) * maxScrollLeft);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    if (dragOffsetRef.current == null) return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const thumbPixelWidth = (thumbWidth / 100) * bounds.width;
    onScrollLeftChange(((event.clientX - bounds.left - dragOffsetRef.current) / Math.max(1, bounds.width - thumbPixelWidth)) * maxScrollLeft);
  }

  function onPointerEnd(event: PointerEvent<HTMLDivElement>) {
    dragOffsetRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  }

  return <div
    aria-label={ariaLabel}
    aria-valuemax={maxScrollLeft}
    aria-valuemin={0}
    aria-valuenow={metrics.scrollLeft}
    className={[styles.root, className].filter(Boolean).join(' ')}
    onKeyDown={onKeyDown}
    onPointerCancel={onPointerEnd}
    onPointerDown={onPointerDown}
    onPointerMove={onPointerMove}
    onPointerUp={onPointerEnd}
    role="scrollbar"
    tabIndex={0}
  ><span className={styles.thumb} style={{ left: `${thumbLeft}%`, width: `${thumbWidth}%` }} /></div>;
}
